// Pre-op: the anaesthetist's clearance, the surgeon's approval and team, the
// checklists, and the WHO Sign-In.
//
// Three gates in order — PAC, approval, final list — each of which moves the
// case's lifecycle. None of them is decided here: every one is a server call
// that either advances the case or explains why it cannot.
//
// The pre-op checklist's four gate items are marked, because those four are what
// hard-block holding-bay check-in. The gate banner comes from the server so the
// screen and the endpoint can never disagree about why a patient is not ready.

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import {
  IPD_COLORS,
  IpdButton,
  IpdFormField,
  IpdSectionCard,
  IpdTabBar,
  IPD_SHAPE,
  IpdTextField,
} from "../../../ipd/utils";
import { formatIstDateTime } from "../../../../utils/istDateTime";
import { teamMembers } from "../../utils/teamMembers";
import {
  addTeamMember,
  approveCase,
  authoriseFinalList,
  fetchAttendeeTypes,
  fetchPac,
  fetchPreopChecklist,
  fetchTeam,
  fetchWhoChecklist,
  recordPac,
  removeTeamMember,
  searchOtStaff,
  savePreopChecklist,
  saveWhoChecklist,
} from "../../api/otApi";
import {
  CheckList,
  DisabledHint,
  DraftBar,
  DraftCheckbox,
  Field,
  FieldGrid,
  GateNotice,
  OtSelect,
  SaveCheckbox,
  SaveTextField,
  errorMessage,
  report,
  reportQuiet,
} from "../common";
import {
  FIRE_RISK_ITEMS,
  LATERALITIES,
  LIFECYCLE_LABELS,
  humanise,
} from "../../constants";
import { canRecordPacOutcome } from "../../utils/pacEligibility";
import PreAnesthesiaAssessmentModal from "../PreAnesthesiaAssessmentModal";

export default function PreopPanel({ otCase, onChanged, active = true }) {
  const [pac, setPac] = useState(null);
  const [team, setTeam] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [who, setWho] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [p, t, c, w] = await Promise.all([
        fetchPac(otCase.id),
        fetchTeam(otCase.id),
        fetchPreopChecklist(otCase.id),
        fetchWhoChecklist(otCase.id),
      ]);
      setPac(p);
      setTeam(t);
      setChecklist(c);
      setWho(w);
    } catch (err) {
      setError(errorMessage(err, "Could not load the pre-op record"));
    }
  }, [otCase.id]);

  // Every case tab stays mounted, so what it read once goes stale when another
  // tab changes the case (consent, checklist, notes): re-read on opening.
  useEffect(() => {
    if (active) load();
  }, [active, load]);

  // Two different kinds of change, and conflating them is what made this
  // screen slow.
  //
  // `refreshAll` is for the three actions that MOVE THE CASE — the PAC
  // outcome, the surgeon's approval, the final-list authorisation. Those
  // change `lifecycle_status`, so the parent has to hear about it, and they
  // happen once per case.
  //
  // Ticking a checklist box changes neither the case nor any of the other
  // three resources on this panel, so it patches in place from what the save
  // returned. It used to cost four GETs and a case reload per tick.
  const refreshAll = async () => {
    await load();
    onChanged();
  };

  const patchChecklist = (type, payload) => {
    setChecklist((current) => ({
      ...current,
      [type === "PREOP" ? "preop" : "daycare"]: payload.checklist,
      gate: payload.gate,
    }));
    // Ticking a shared item changes the WHO Sign-In too. The save returns
    // the updated row, so the card beneath updates without a refetch.
    if (payload.who_checklist) setWho(payload.who_checklist);
  };

  if (error) {
    return <Typography sx={{ color: IPD_COLORS.error.text }}>{error}</Typography>;
  }

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <PacCard otCase={otCase} pac={pac} onDone={refreshAll} />
      <DayOfSurgeryReassessmentCard otCase={otCase} pac={pac} onDone={refreshAll} />
      <TeamCard otCase={otCase} team={team} onDone={refreshAll} />
      <ApprovalCard otCase={otCase} onDone={refreshAll} />
      <ChecklistCard otCase={otCase} checklist={checklist} onPatch={patchChecklist} />
      <WhoCard otCase={otCase} who={who} sections={["SIGN_IN"]} onPatch={setWho} />
    </Box>
  );
}

function PacCard({ otCase, pac, onDone }) {
  const [open, setOpen] = useState(false);

  const current = pac?.current;
  // The server refuses a PAC outcome once a case is cancelled, closed, in
  // theatre or in recovery (manage_preop.CasePacView.post). The form still
  // opens there — it is also where the assessment and the consent are printed —
  // it just cannot save. DRAFT is not locked here even though
  // canRecordPacOutcome says so: a draft case takes a PAC (it moves to PAC_DONE
  // or POSTPONED), and this tab has always allowed it.
  const locked =
    otCase.lifecycle_status !== "DRAFT" && !canRecordPacOutcome(otCase.lifecycle_status);
  const lockedReason = locked
    ? `PAC evaluation locked while case is in stage: ${
        LIFECYCLE_LABELS[otCase.lifecycle_status] || otCase.lifecycle_status
      }`
    : "";

  return (
    <IpdSectionCard
      title="Pre-Anesthetic Assessment & Surgical Clearance"
      subtitle="Pre-anesthesia evaluation determines clinical fitness and authorizes case clearance, deferral, or medical optimization."
      actions={
        <Box sx={{ textAlign: "right" }}>
          <Button size="small" variant="contained" onClick={() => setOpen(true)}>
            {locked ? "View PAC Evaluation" : "Record PAC Evaluation"}
          </Button>
          {locked && (
            <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.warning.text, mt: 0.5 }}>
              {lockedReason}
            </Typography>
          )}
        </Box>
      }
    >
      {!otCase.pac_required && (
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mb: 1.5 }}>
          Pre-anesthetic clinic evaluation not required for this minor/local procedure; eligible for direct surgical scheduling.
        </Typography>
      )}
      {current ? (
        <FieldGrid columns={4}>
          <Field label="Outcome" value={current.outcome} />
          <Field label="ASA class" value={current.asa_display} />
          <Field label="Assessed by" value={current.assessed_by_name} />
          <Field label="Evaluation Date & Time" value={formatIstDateTime(current.assessed_at)} />
          <Field label="Notes" value={current.notes} />
        </FieldGrid>
      ) : (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          No pre-anesthetic clearance documented yet.
        </Typography>
      )}

      {(pac?.history || []).length > 1 && (
        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 1 }}>
            Historical Pre-Anesthesia Evaluations
          </Typography>
          {pac.history
            .filter((h) => !h.active)
            .map((h) => (
              <Typography
                key={h.id}
                sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, mb: 0.5 }}
              >
                {formatIstDateTime(h.assessed_at)} — <strong>{h.outcome}</strong>
                {h.notes ? ` — ${h.notes}` : ""}
              </Typography>
            ))}
        </Box>
      )}

      {open && (
        <PreAnesthesiaAssessmentModal
          otCase={otCase}
          lockedReason={lockedReason}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            onDone();
          }}
        />
      )}
    </IpdSectionCard>
  );
}

/**
 * Day-of-Surgery Pre-Induction Reassessment Card (NABH COP.13.d).
 *
 * Mandatory evaluation on the day of surgery before patient is wheeled into OT.
 * Verifies same-day baseline vitals, ISA fasting compliance, airway/dental status,
 * and confirms fitness for induction of anaesthesia.
 */
function DayOfSurgeryReassessmentCard({ otCase, pac, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const current = pac?.current;
  const reassessmentNotes = current?.reassessment_notes || "";

  // Vitals start from the PAC's own readings, never invented ones.
  const [form, setForm] = useState({
    pulse: current?.vitals?.pulse || "",
    bp: current?.vitals?.bp || "",
    spo2: current?.vitals?.spo2 || "",
    temp: current?.vitals?.temp || "",
    fasting_verified: true,
    dental_verified: true,
    notes: reassessmentNotes || "Patient re-evaluated on day of surgery. Fasting verified (NPO > 6 hrs). Dentition checked; loose teeth accounted for. Vitals stable. Cleared for induction of anaesthesia.",
  });

  const handleSave = async () => {
    setBusy(true);
    try {
      // Re-records the PAC's own decision; the card is only offered once there
      // is one, so it can never clear a patient on its own.
      const payload = {
        outcome: current.outcome,
        asa_class: current.asa_class,
        asa_emergency: Boolean(current.asa_emergency),
        reassessment_notes: form.notes,
        notes: current?.notes || form.notes,
        vitals: {
          pulse: form.pulse,
          bp: form.bp,
          spo2: form.spo2,
          temp: form.temp,
        },
      };
      const res = await recordPac(otCase.id, payload);
      if (report(res)) {
        toast.success("Day-of-surgery pre-induction reassessment recorded (NABH COP.13.d)");
        setOpen(false);
        onDone && onDone();
      }
    } catch (err) {
      toast.error(errorMessage(err, "Failed to record day-of-surgery reassessment"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <IpdSectionCard
      title="Day-of-Surgery Reassessment (NABH COP.13.d)"
      subtitle="Mandatory same-day pre-induction check before wheeling into the Operating Theatre."
      actions={
        current ? (
          <Button size="small" variant="contained" onClick={() => setOpen(true)}>
            {reassessmentNotes ? "Update Reassessment" : "Record Same-Day Reassessment"}
          </Button>
        ) : null
      }
    >
      {reassessmentNotes ? (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Box sx={{ bgcolor: "#ECFDF5", color: "#047857", px: 1.2, py: 0.3, borderRadius: 1, fontWeight: 700, fontSize: 12 }}>
              ✓ PRE-INDUCTION EVALUATION COMPLETE
            </Box>
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
              Recorded by {current?.assessed_by_name || "Anaesthetist"}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 13, bgcolor: "#F8FAFC", p: 1.2, borderRadius: 1, border: `1px solid ${IPD_COLORS.border}` }}>
            {reassessmentNotes}
          </Typography>
          {current?.vitals && (
            <Typography sx={{ mt: 1, fontSize: 12, color: IPD_COLORS.textSecondary }}>
              Same-day Vitals: Pulse {current.vitals.pulse || "—"} bpm · BP {current.vitals.bp || "—"} mmHg · SpO₂ {current.vitals.spo2 || "—"}%
            </Typography>
          )}
        </Box>
      ) : (
        <Box sx={{ p: 1.5, border: `1px dashed ${IPD_COLORS.border}`, borderRadius: 1 }}>
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
            {current
              ? "Awaiting day-of-surgery pre-induction evaluation. The anaesthetist must verify NPO fasting, dentition, and same-day vitals before patient is transferred to OR."
              : "The PAC must be recorded before the day-of-surgery reassessment."}
          </Typography>
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Day-of-Surgery Pre-Induction Reassessment</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, mb: 2 }}>
            NABH Standard COP.13.d mandates recording an immediate pre-induction reassessment confirming fasting compliance, dental chart status, and baseline vital signs.
          </Typography>
          <Box sx={{ display: "grid", gap: 2 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 1.5 }}>
              <IpdFormField label="Pulse (bpm)">
                <IpdTextField
                  value={form.pulse}
                  onChange={(e) => setForm((f) => ({ ...f, pulse: e.target.value }))}
                />
              </IpdFormField>
              <IpdFormField label="BP (mmHg)">
                <IpdTextField
                  value={form.bp}
                  onChange={(e) => setForm((f) => ({ ...f, bp: e.target.value }))}
                />
              </IpdFormField>
              <IpdFormField label="SpO₂ (%)">
                <IpdTextField
                  value={form.spo2}
                  onChange={(e) => setForm((f) => ({ ...f, spo2: e.target.value }))}
                />
              </IpdFormField>
              <IpdFormField label="Temp (°C)">
                <IpdTextField
                  value={form.temp}
                  onChange={(e) => setForm((f) => ({ ...f, temp: e.target.value }))}
                />
              </IpdFormField>
            </Box>

            <Box sx={{ bgcolor: "#F8FAFC", p: 1.5, borderRadius: 1, border: `1px solid ${IPD_COLORS.border}`, display: "grid", gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.fasting_verified}
                    onChange={(e) => setForm((f) => ({ ...f, fasting_verified: e.target.checked }))}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    ISA Fasting Compliance Verified (≥ 2 hrs clear water, ≥ 6 hrs light meal)
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.dental_verified}
                    onChange={(e) => setForm((f) => ({ ...f, dental_verified: e.target.checked }))}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    Dentition & Airway Re-evaluated (loose teeth accounted for; airway patent)
                  </Typography>
                }
              />
            </Box>

            <IpdFormField label="Pre-Induction Anaesthetic Notes & Confirmation" required>
              <IpdTextField
                multiline
                rows={3}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </IpdFormField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton disabled={busy} onClick={handleSave}>
            {busy ? "Saving..." : "Sign & Record Reassessment"}
          </IpdButton>
        </DialogActions>
      </Dialog>
    </IpdSectionCard>
  );
}


/**
 * Who is doing what on this case — one tab per role.
 *
 * Was a form with two dropdowns and a typed name. Tabs instead, because the
 * question is really "is every role filled", and a list of assignments answers
 * that far worse than a strip you can see the gaps in.
 *
 * The doctor-or-nurse dropdown is gone: the Case Role master now says which
 * staff list fills each role, so the name field searches the right people
 * instead of asking a question with one right answer. The person is stored with
 * their id, so "Dr Sen" and "Dr R. Sen" stop being two different surgeons in
 * the surgeon-wise register.
 */
function TeamCard({ otCase, team, onDone }) {
  const [types, setTypes] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ member_name: "", member_id: "", grade: "" });
  const [matches, setMatches] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchAttendeeTypes({ active_only: 1 })
      .then((rows) => {
        setTypes(rows || []);
        setActive((current) => current ?? (rows || [])[0]?.id ?? null);
      })
      .catch(() => setTypes([]));
  }, []);

  const role = types.find((t) => t.id === active);
  // The endpoint returns {team, missing_mandatory_roles}, not a bare array.
  // See utils/teamMembers -- filtering the object directly is what blanked
  // this tab.
  const members = teamMembers(team);
  const assigned = members.filter((m) => m.attendee_type_id === active);

  // Search the staff list the role is filled from. A role nobody has
  // classified searches both, which is the honest answer for it. A name
  // picked from the list is not searched again -- that re-opened the list
  // after every pick.
  useEffect(() => {
    const q = form.member_name.trim();
    if (!role || q.length < 2 || form.picked) {
      setMatches([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      searchOtStaff({ q, member_type: role.filled_by || "" })
        .then((rows) => setMatches(rows || []))
        .catch(() => setMatches([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [form.member_name, form.picked, role]);

  const assign = async () => {
    if (!role || !form.member_name.trim()) return;
    setBusy(true);
    try {
      const res = await addTeamMember(otCase.id, {
        attendee_type_id: role.id,
        member_name: form.member_name.trim(),
        member_id: form.member_id || undefined,
        // Only sent when the role has not been classified; otherwise the
        // server takes it from the role and ignores this.
        member_type: role.filled_by || "DOCTOR",
        grade: form.grade.trim() || undefined,
      });
      if (report(res)) {
        setForm({ member_name: "", member_id: "", grade: "" });
        setMatches([]);
        onDone();
      }
    } finally {
      setBusy(false);
    }
  };

  const filled = (typeId) => members.some((m) => m.attendee_type_id === typeId);

  return (
    <IpdSectionCard
      title="Surgical & Anesthetic Clinical Team Roster"
      subtitle="Documented roster of lead surgeons, anesthesiologists, surgical assistants, and scrub/circulating nurses."
    >
      {types.length === 0 ? (
        <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary }}>
          No surgical team roles are configured yet — configure team roles in OT Masters.
        </Typography>
      ) : (
        <>
          {/* Mandatory roles carry a dot until they are filled, so the gaps are
              visible without opening every tab. */}
          <IpdTabBar
            tabs={types.map((t) => ({
              id: t.id,
              label: `${t.name}${t.is_mandatory && !filled(t.id) ? " •" : ""}`,
            }))}
            activeTab={active}
            onChange={setActive}
            ariaLabel="Team roles"
          />

          {role && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1.5 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                  {role.name}
                </Typography>
                {role.is_mandatory && (
                  <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.warning.text, fontWeight: 600 }}>
                    Mandatory
                  </Typography>
                )}
              </Box>

              {assigned.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mb: 2 }}>
                  No personnel assigned to this clinical role yet.
                </Typography>
              ) : (
                assigned.map((m) => (
                  <Box
                      key={m.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 0.75,
                        borderBottom: `1px solid ${IPD_COLORS.border}`,
                      }}
                    >
                      <Typography sx={{ fontSize: 13.5, flex: 1 }}>{m.member_name}</Typography>
                      {m.role_description && (
                        <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                          {m.role_description}
                        </Typography>
                      )}
                      <Button
                        size="small"
                        color="error"
                        onClick={async () => {
                          if (report(await removeTeamMember(m.id))) onDone();
                        }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))
              )}

              <Box
                component="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  assign();
                }}
                sx={{
                  mt: 2,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "2fr 1fr auto" },
                  gap: 2,
                  alignItems: "end",
                }}
              >
                <IpdFormField label="Name">
                  <IpdTextField
                    fullWidth
                    placeholder="Search clinical staff directory..."
                    value={form.member_name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, member_name: e.target.value, member_id: "", picked: false }))
                    }
                  />
                </IpdFormField>
                {/* The card hides whatever overflows it, which cut a floating
                    list off at its bottom edge. The list takes a row of its own
                    instead: under the name on a phone, under all three fields
                    on a wide screen. */}
                {matches.length > 0 && (
                  <Box
                    sx={{
                      gridColumn: "1 / -1",
                      gridRow: { md: 2 },
                      maxHeight: 210,
                      overflowY: "auto",
                      bgcolor: IPD_COLORS.cardBg,
                      border: `1px solid ${IPD_COLORS.border}`,
                      borderRadius: `${IPD_SHAPE.radiusMd}px`,
                      boxShadow: IPD_COLORS.shadow.menu,
                    }}
                  >
                    {matches.map((p) => (
                      <Box
                        key={`${p.member_type}-${p.member_id}-${p.member_name}`}
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            member_name: p.member_name,
                            member_id: p.member_id || "",
                            picked: true,
                          })) || setMatches([])
                        }
                        sx={{
                          px: 1.5,
                          py: 1,
                          cursor: "pointer",
                          "&:hover": { bgcolor: IPD_COLORS.pageBg },
                        }}
                      >
                        <Typography sx={{ fontSize: 13 }}>{p.member_name}</Typography>
                        {p.detail && (
                          <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>
                            {p.detail}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

                <IpdFormField label={role.grading_required ? "Clinical Grade / Level (Required)" : "Clinical Grade / Level"}>
                  <IpdTextField
                    fullWidth
                    value={form.grade}
                    onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))}
                  />
                </IpdFormField>

                <IpdButton disabled={busy || !form.member_name.trim()} onClick={assign}>
                  Assign Team Member
                </IpdButton>
              </Box>
            </Box>
          )}
        </>
      )}
    </IpdSectionCard>
  );
}

function ApprovalCard({ otCase, onDone }) {
  return (
    <IpdSectionCard
      title="Surgical Sign-off & Operating List Publication"
      subtitle="Primary surgeon clinical confirmation and OT In-Charge operational authorization for theatre list inclusion."
    >
      <FieldGrid columns={4}>
        <Field label="Approved by" value={otCase.approved_by_name} />
        <Field label="Approved at" value={formatIstDateTime(otCase.approved_at)} />
        <Field label="Listed by" value={otCase.listed_by_name} />
        <Field label="Listed at" value={formatIstDateTime(otCase.listed_at)} />
      </FieldGrid>
      <Box sx={{ mt: 2, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        <DisabledHint
          reason={
            Boolean(otCase.approved_at) &&
            `This case was already authorized on ${formatIstDateTime(otCase.approved_at)}.`
          }
        >
          <Button
            size="small"
            variant="contained"
            disabled={Boolean(otCase.approved_at)}
            onClick={async () => {
              if (report(await approveCase(otCase.id))) onDone();
            }}
          >
            Authorize Surgical Case
          </Button>
        </DisabledHint>
        <DisabledHint
          reason={
            Boolean(otCase.listed_at) &&
            `This case was already published to the final list on ${formatIstDateTime(otCase.listed_at)}.`
          }
        >
          <Button
            size="small"
            variant="outlined"
            disabled={Boolean(otCase.listed_at)}
            onClick={async () => {
              if (report(await authoriseFinalList(otCase.id))) onDone();
            }}
          >
            Publish to Final Operating List
          </Button>
        </DisabledHint>
      </Box>
      <Typography sx={{ mt: 1.5, fontSize: 12, color: IPD_COLORS.textSecondary }}>
        Pre-anesthesia clinic clearance is mandatory prior to surgical approval. Final list publishing is an authorized OT In-Charge governance function.
      </Typography>
    </IpdSectionCard>
  );
}

/**
 * The record behind the "site marked" tick.
 *
 * The Universal Protocol asks for the site to be marked by the person
 * performing the procedure, with the patient taking part where they can. A
 * boolean cannot answer any of that, and a wrong-site enquiry asks for all of
 * it. Laterality is a dropdown rather than a text box because "R", "Rt" and
 * "right" are the same answer written three ways, and a wrong-side review
 * cannot filter on prose.
 *
 * Only shown once the box is ticked: asking who marked a site nobody has marked
 * yet is a question with no answer.
 */
function SiteMarkingRecord({ row, onSave }) {
  const marking = row?.site_marking || {};

  if (!row?.values?.site_marked) return null;

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 1.5,
        border: `1px solid ${IPD_COLORS.border}`,
        backgroundColor: IPD_COLORS.pageBg,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1.5 }}>
        Surgical Site Marking & Laterality Verification
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "180px 1fr" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <IpdFormField label="Surgical Laterality">
          <OtSelect
            value={marking.laterality || ""}
            onChange={(e) => onSave({ surgical_laterality: e.target.value })}
            options={LATERALITIES}
            placeholder="Laterality Not Specified"
          />
        </IpdFormField>
        <IpdFormField label="Marked by">
          <SaveTextField
            placeholder="Primary Operating Surgeon"
            value={marking.marked_by_name || ""}
            onSave={(name) => onSave({ site_marked_by_name: name })}
          />
        </IpdFormField>
      </Box>
      <SaveCheckbox
        checked={marking.patient_participated}
        label="Patient actively verified and participated in surgical site marking"
        onSave={(next) => onSave({ site_marking_patient_participated: next })}
      />
      {marking.marked_at && (
        <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>
          Marked {formatIstDateTime(marking.marked_at)}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The ward pre-op checklist, and the daycare-suitability one.
 *
 * Every tick saves on its own and PATCHES the panel from what came back, rather
 * than re-fetching it. The four gate items are given their own group instead of
 * an asterisk and a footnote — they are the ones that stop the patient leaving
 * the ward, and that is worth a heading rather than a symbol.
 */
export function ChecklistCard({ otCase, checklist, onPatch, bare = false }) {
  // Ticks wait here and go up in one request. Keyed by checklist type, so the
  // pre-op list and the daycare list keep their own pending set and their own
  // Save -- they are two separate pieces of paper and two separate signatures.
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(null);

  const pending = (type) => draft[type] || {};

  const save = async (type, values, extra) => {
    // Whatever is pending rides along with whatever prompted this call, so
    // co-signing or recording the site marking can never strand a tick.
    const merged = { ...pending(type), ...values };
    setSaving(type);
    const res = await savePreopChecklist(otCase.id, {
      checklist_type: type,
      values: merged,
      ...extra,
    });
    setSaving(null);
    if (!reportQuiet(res)) return false;
    onPatch(type, res.data);
    setDraft((d) => ({ ...d, [type]: {} }));
    return true;
  };

  const tick = (type, item, next) =>
    setDraft((d) => ({ ...d, [type]: { ...(d[type] || {}), [item]: next } }));

  const render = (type, row) => {
    if (!row) {
      return (
        <Button size="small" variant="outlined" onClick={() => save(type, {})}>
          Initiate {type === "PREOP" ? "Pre-Operative Nursing" : "Daycare Suitability"} Checklist
        </Button>
      );
    }

    const gateItems = row.gate_items || [];
    // What the box shows: the pending tick if there is one, else the server's.
    const shown = (item) => pending(type)[item] ?? row.values[item];
    const unsaved = Object.keys(pending(type)).filter(
      (i) => Boolean(pending(type)[i]) !== Boolean(row.values[i])
    ).length;
    const groups =
      type === "PREOP"
        ? [
            {
              key: "gate",
              title: "MANDATORY PRE-TRANSFER REQUIREMENTS (WARD GATE)",
              blocking: "Mandatory verification criteria required prior to Holding Bay transfer",
              items: row.items.filter((i) => gateItems.includes(i)),
            },
            {
              key: "rest",
              title: "PRE-OPERATIVE CLINICAL & NURSING PREPARATION",
              items: row.items.filter((i) => !gateItems.includes(i)),
            },
          ]
        : [{ key: "daycare", title: null, items: row.items }];

    return (
      <>
        <Box sx={{ display: "grid", gap: 2 }}>
          {groups.map((group) => (
            <CheckList
              key={group.key}
              title={group.title}
              blocking={group.blocking}
              done={group.items.filter((i) => shown(i)).length}
              total={group.items.length}
            >
              {group.items.map((item) => (
                <DraftCheckbox
                  key={item}
                  checked={shown(item)}
                  strong={gateItems.includes(item)}
                  label={humanise(item)}
                  disabled={saving === type}
                  onChange={(next) => tick(type, item, next)}
                />
              ))}
            </CheckList>
          ))}
        </Box>

        <DraftBar
          count={unsaved}
          busy={saving === type}
          onSave={() => save(type, {})}
          onDiscard={() => setDraft((d) => ({ ...d, [type]: {} }))}
        />

        {type === "PREOP" && (
          <SiteMarkingRecord row={row} onSave={(patch) => save(type, {}, patch)} />
        )}

        <Box
          sx={{
            mt: 2,
            pt: 1.5,
            borderTop: `1px solid ${IPD_COLORS.border}`,
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
            {row.completed
              ? `Completed by ${row.completed_by_name || "—"}, ${formatIstDateTime(
                  row.completed_at
                )}`
              : "Pending Completion"}
          </Typography>
          {row.completed && !row.co_signed_at && (
            <Button size="small" onClick={() => save(type, {}, { co_sign: true })}>
              Co-Sign Verification
            </Button>
          )}
          {row.co_signed_at && (
            <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
              Co-signed by {row.co_signed_by_name}
            </Typography>
          )}
        </Box>
      </>
    );
  };

  return (
    <IpdSectionCard title="Pre-Operative Nursing Preparation Checklist">
      <Box sx={{ mb: 2 }}>
        <GateNotice
          gate={checklist?.gate}
          readyMessage="Mandatory Pre-Op Checklist Completed — Patient authorized for transfer to Pre-Operative Holding Bay."
        />
      </Box>
      {render("PREOP", checklist?.preop)}

      {checklist?.daycare_applicable && (
        <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${IPD_COLORS.border}` }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 1.5 }}>
            Daycare Clinical Suitability Criteria
          </Typography>
          {render("DAYCARE", checklist?.daycare)}
        </Box>
      )}
    </IpdSectionCard>
  );
}

/** Which sections belong to which moment. */
const WHO_META = {
  SIGN_IN: { label: "Sign In", when: "Prior to induction of anaesthesia" },
  TIME_OUT: { label: "Time Out", when: "Prior to surgical skin incision" },
  SIGN_OUT: { label: "Sign Out", when: "Prior to transfer out of the Operating Theatre" },
};

/**
 * The WHO Surgical Safety Checklist.
 *
 * Reused by the theatre panel for the two sections completed in the room, which
 * is why the sections are a prop. Like the pre-op checklist, each tick saves and
 * patches rather than re-fetching — this card carries up to seventeen boxes, and
 * re-loading seven endpoints after each one is what made the Time-Out feel like
 * it had hung.
 */
export function WhoCard({ otCase, who, sections, onPatch }) {
  // One Save per section. A Time Out is read out to the room as a single
  // sequence and answered as one, so it is written back as one.
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(null);

  const pending = (section) => draft[section] || {};

  const save = async (section, values) => {
    const merged = { ...pending(section), ...values };
    setSaving(section);
    const res = await saveWhoChecklist(otCase.id, { section, values: merged });
    setSaving(null);
    if (!reportQuiet(res)) return false;
    onPatch(res.data);
    setDraft((d) => ({ ...d, [section]: {} }));
    return true;
  };

  const tick = (section, item, next) =>
    setDraft((d) => ({ ...d, [section]: { ...(d[section] || {}), [item]: next } }));

  return (
    <IpdSectionCard title="WHO Surgical Safety Checklist">
      <Box sx={{ display: "grid", gap: 3 }}>
        {sections.map((section) => {
          const row = who?.sections?.[section];
          const meta = WHO_META[section];
          const items = row?.items || [];
          // `row.values` is read through the row, not off it: this is only
          // safe today because `items` is empty whenever `row` is missing,
          // and that is an accident rather than a guarantee.
          const values = row?.values || {};
          // Risk flags live beside the checkpoints and save through the same
          // call, so they share the pending set. The key spaces are disjoint.
          const risks = row?.risks || {};
          const shown = (item) => pending(section)[item] ?? values[item] ?? risks[item];
          const done = items.filter((i) => shown(i)).length;
          const unsaved = Object.keys(pending(section)).filter(
            (i) => Boolean(pending(section)[i]) !== Boolean(values[i] ?? risks[i])
          ).length;

          return (
            <Box key={section}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 1.25,
                  mb: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{meta.label}</Typography>
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                  {meta.when}
                </Typography>
                {row?.completed && (
                  <Chip
                    size="small"
                    label={`Done ${formatIstDateTime(row.completed_at)}`}
                    sx={{
                      height: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      color: IPD_COLORS.success.text,
                      backgroundColor: IPD_COLORS.success.bg,
                    }}
                  />
                )}
              </Box>

              {!row ? (
                <Button size="small" variant="outlined" onClick={() => save(section, {})}>
                  Initiate {meta.label}
                </Button>
              ) : (
                <>
                  <CheckList done={done} total={items.length}>
                    {items.map((item) => {
                      // Three Sign-In items are the ward's answers,
                      // mirrored from the pre-op checklist. Shown so the
                      // section reads complete, disabled so nobody ticks
                      // the same fact twice.
                      const fromPreop = (row.derived_from_preop || {})[item];
                      return (
                        <DraftCheckbox
                          key={item}
                          checked={shown(item)}
                          disabled={Boolean(fromPreop) || saving === section}
                          hint={fromPreop ? "Imported from Pre-Op Nursing Checklist" : undefined}
                          label={humanise(item.replace(/^(si|to|so)_/, ""))}
                          onChange={(next) => tick(section, item, next)}
                        />
                      );
                    })}
                  </CheckList>

                  {row.risks && Object.keys(row.risks).length > 0 && (
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1.5,
                        borderRadius: 1.5,
                        border: `1px solid ${row.fire_risk_high ? IPD_COLORS.error.border : IPD_COLORS.border}`,
                        backgroundColor: row.fire_risk_high ? IPD_COLORS.error.bg : IPD_COLORS.pageBg,
                      }}
                    >
                      <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.25 }}>
                        {section === "TIME_OUT" ? "FIRE RISK" : "RISKS"}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted, mb: 0.5 }}>
                        Advisory clinical indicators &mdash; recorded for perioperative situational awareness.
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                          columnGap: 2,
                        }}
                      >
                        {Object.keys(row.risks).map((item) => (
                          <DraftCheckbox
                            key={item}
                            checked={shown(item)}
                            disabled={saving === section}
                            label={
                              FIRE_RISK_ITEMS.find((f) => f.key === item)?.label ||
                              humanise(item.replace(/^(si|to|so)_/, ""))
                            }
                            onChange={(next) => tick(section, item, next)}
                          />
                        ))}
                      </Box>
                      {/* All three of the triad in one field is the recognised
                          high-risk configuration, so it is said rather than left
                          for the reader to notice three ticked boxes. */}
                      {row.fire_risk_high && (
                        <Typography
                          sx={{ mt: 1, fontSize: 12.5, fontWeight: 700, color: IPD_COLORS.error.text }}
                        >
                          HIGH FIRE RISK — an oxidiser, an ignition source and a fuel are all
                          present in this field.
                        </Typography>
                      )}
                    </Box>
                  )}

                  <DraftBar
                    count={unsaved}
                    busy={saving === section}
                    onSave={() => save(section, {})}
                    onDiscard={() => setDraft((d) => ({ ...d, [section]: {} }))}
                  />
                </>
              )}
            </Box>
          );
        })}
      </Box>
    </IpdSectionCard>
  );
}
